import { Formik, FormikProps } from 'formik';
import _ from 'lodash';
import { useState } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Redirect, useLocation, useParams } from 'react-router-dom';

import { Button, Typography } from '@vivaahealth/design-system';

import { NEW_SUBSCRIBER_SCHEMA, SubscriberUpload } from './schema';
import styles from './subscriber.module.css';

import { getBlankSubscriber } from '../../FormDetailsPage/utils';
import { parseFieldValue } from '../../MultiInsurance';
import { maybeUpdateInsuranceDetails } from '../../MultiInsurance/utils';

import * as api from '../../../../../api';
import {
  getSubscribersFromGuarantorsPrimitive,
  mergeValues,
} from '../../../../../components/Guarantor/utils';
import * as store from '../../../../../reducers/store';
import {
  getSelectedForms,
  getSelectedPracticePreference,
} from '../../../../../utils/formSet';
import SelectSubscriberForm from './SelectSubscriberForm';

interface StateProps {
  forms: store.Forms;
  employers: store.Loaded['masterPracticePreference']['employers'];
  employmentStatuses: store.Loaded['masterPracticePreference']['employmentStatuses'];
}

interface SubscriberPageProps extends WithTranslation, StateProps {
  cards: api.PatientInsuranceInformation.InsuranceInformationWithDetails[];
  encounterId: string;
  field: api.PrevisitFormField.PrevisitFormFieldWithConfig;
  ocrResults: api.InsuranceCardOcrResult.InsuranceCardOcrResult | null;
  onSubmit: (
    subscribers: api.PrevisitPatientInsurance.PrevisitSubscriber[],
    subscriberId: string,
    insuranceInformationId: string,
  ) => Promise<void>;
}

const SubscriberPage = ({
  cards,
  encounterId,
  field,
  ocrResults,
  forms,
  employmentStatuses,
  employers,
  t,
  onSubmit,
}: SubscriberPageProps) => {
  const { insuranceInformationId } = useParams<{
    insuranceInformationId: string;
  }>();
  const location = useLocation();
  const [newSubscriber, setNewSubscriber] = useState(false);

  const card = cards.find(
    ({ patientInsuranceInformationId }) =>
      patientInsuranceInformationId === insuranceInformationId,
  );

  if (!card) {
    return <Redirect to={'../' + location.search} />;
  }

  const parsedValue = parseFieldValue(field.value);
  const guarantorSubscribers = getSubscribersFromGuarantorsPrimitive(forms);
  const mergedSubscribers = mergeValues(
    parsedValue.subscribers,
    guarantorSubscribers,
  );
  const subscribersById = _.mapKeys(mergedSubscribers, 'id');
  const subscriberOrdering = mergedSubscribers.map((g) => g.id);

  const subscriber = _.find(
    subscribersById,
    (s) =>
      s.patientRelationshipToPrimaryPolicyholder ===
      api.Common.Relationship.Self,
  );

  const initialValues: SubscriberUpload = {
    ...getBlankSubscriber(),
    subscriberId: subscriber?.id ?? null,
  };

  return (
    <Formik
      validateOnMount
      initialValues={initialValues}
      validationSchema={NEW_SUBSCRIBER_SCHEMA(
        t,
        employmentStatuses,
        employers as any,
        newSubscriber,
      )}
      onSubmit={async (values) => {
        const subscriberId = values.subscriberId!;
        const subscriberExists = subscriberId in subscribersById;

        if (!subscriberExists) {
          subscribersById[subscriberId] = {
            ...values,
            id: subscriberId,
            newlyAdded: true,
            patientRelationshipToPrimaryPolicyholder:
              values.patientRelationshipToPrimaryPolicyholder || null,
          };
          subscriberOrdering.push(subscriberId);
        }

        const insuranceCoverage: api.PrevisitPatientInsurance.InsuranceCoverage =
          {
            coverageStartDate: card.coverageStartDate,
            groupNumber: card.groupNumber,
            id: card.id,
            insuranceChanges: card.insuranceChanges,
            payor: card.payor,
            planDescription: card.planDescription,
            primary: card.primary,
            primaryPolicyHolderMemberId: card.primaryPolicyHolderMemberId,
            subscriberId: values.subscriberId,
          };
        const subscribers = subscriberOrdering
          .map((subId) => {
            const subscriber = subscribersById[subId];
            return {
              ..._.omit(subscriber, 'newlyAdded'),
              // trim string values
              primaryPolicyHolderFirstName:
                subscriber.primaryPolicyHolderFirstName?.trim() ?? null,
              primaryPolicyHolderLastName:
                subscriber.primaryPolicyHolderLastName?.trim() ?? null,
              primaryPolicyHolderEmployer:
                subscriber.primaryPolicyHolderEmployer?.trim() ?? null,
            };
          })
          .filter((subscriber) =>
            // currently selected subscriber should be valid, so clean up invalid entries
            _.every([
              subscriber.primaryPolicyHolderFirstName,
              subscriber.primaryPolicyHolderLastName,
              subscriber.id,
            ]),
          );

        const valueToSubmit = {
          ...parsedValue,
          subscribers,
          insuranceCoverages: [insuranceCoverage],
        };

        await maybeUpdateInsuranceDetails(
          valueToSubmit,
          ocrResults ?? undefined,
          encounterId,
        );
        await onSubmit(subscribers, subscriberId, insuranceInformationId);
      }}
    >
      {(formikProps: FormikProps<SubscriberUpload>) => {
        return (
          <div className={styles.container}>
            <Typography variant="headline-small" bold>
              {t('title')}
            </Typography>
            <SelectSubscriberForm
              formikProps={formikProps}
              mergedSubscribers={mergedSubscribers}
              employmentStatuses={employmentStatuses}
              employers={employers as any}
              newSubscriber={newSubscriber}
              setNewSubscriber={setNewSubscriber}
            />
            <Button
              data-testid="finish-adding-card"
              disabled={!formikProps.isValid || formikProps.isSubmitting}
              fullWidth
              onClick={formikProps.submitForm}
            >
              {t('finish')}
            </Button>
          </div>
        );
      }}
    </Formik>
  );
};

const mapStateToProps = (state: store.FormSetSelected): StateProps => {
  const selectedPractice = getSelectedPracticePreference(state);
  return {
    employers: selectedPractice.employers,
    employmentStatuses: selectedPractice.employmentStatuses,
    forms: getSelectedForms(state.formSets, state.selectedFormSetId),
  };
};

export default connect(mapStateToProps)(
  withTranslation('subscriberPage')(SubscriberPage),
);
